import React from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { Tooltip } from "@mui/material";
import SectionGlyph from "./SectionGlyph";
import {
  AlertCircleIcon,
  Alert02Icon,
  Tick01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import ViolationStatus from "enums/ViolationStatus";
import { useTranslation } from "react-i18next";

export type PolicyIconType =
  | "receipt"
  | "allocation"
  | "category"
  | "attendee"
  | "approval";

interface PolicyIconProps {
  violationStatus?: string;
  violationType?: PolicyIconType;
  isExempt?: boolean;
  /** Glyph size in px. */
  size?: number;
}

const getTooltipText = (
  t: (key: string) => string,
  status?: string,
  type?: PolicyIconType,
  isExempt?: boolean,
): string | null => {
  if (isExempt) return null;

  switch (status) {
    case ViolationStatus.MISSING:
      switch (type) {
        case "receipt":
          return t("Missing receipt");
        case "allocation":
          return t("Missing allocation");
        case "category":
          return t("Missing category");
        case "attendee":
          return t("Missing attendees");
        case "approval":
          return t("Pending approval");
        default:
          return t("Missing");
      }
    case ViolationStatus.LOST:
      return t("MarkedAsLostAwaitingApproval");
    case ViolationStatus.MISMATCH:
      switch (type) {
        case "receipt":
          return t("ReceiptMismatchAwaitingApproval");
        default:
          return t("Mismatch");
      }
    default:
      return null;
  }
};

type PolicyTone = "success" | "warning" | "danger";

/**
 * The tint and glyph colour per tone. Set here rather than through the icon
 * button's `color` prop because the DS has no success or warning family for
 * that control, only default, primary, secondary and danger. Doing the two
 * available tones natively and the rest by hand would put two different tint
 * systems side by side in one row.
 */
const TONE_TOKENS: Record<PolicyTone, { background: string; color: string }> = {
  success: {
    background: "var(--color-status-success-bg)",
    color: "var(--color-status-success-text)",
  },
  warning: {
    background: "var(--color-status-warning-bg)",
    color: "var(--color-status-warning-text)",
  },
  danger: {
    background: "var(--color-status-danger-bg)",
    color: "var(--color-status-danger-text)",
  },
};

/** The glyph and its tone, keyed on what the policy check found. */
const glyphFor = (
  violationStatus?: string,
  isExempt?: boolean,
): { icon: IconSvgElement; tone: PolicyTone } => {
  if (isExempt) {
    return { icon: Tick01Icon, tone: "success" };
  }

  switch (violationStatus) {
    case ViolationStatus.MISSING:
      return { icon: AlertCircleIcon, tone: "danger" };
    // Lost and mismatched are both awaiting an approval decision, so they are
    // neither satisfied nor missing.
    case ViolationStatus.LOST:
      return { icon: AlertCircleIcon, tone: "warning" };
    case ViolationStatus.MISMATCH:
      return { icon: Alert02Icon, tone: "warning" };
    default:
      return { icon: Tick01Icon, tone: "success" };
  }
};

const PolicyIcon: React.FC<PolicyIconProps> = ({
  violationStatus,
  violationType,
  isExempt = false,
  size = 18,
}) => {
  const { t } = useTranslation();
  const tooltipText = getTooltipText(
    t,
    violationStatus,
    violationType,
    isExempt,
  );

  const { icon, tone } = glyphFor(violationStatus, isExempt);
  const { background, color } = TONE_TOKENS[tone];

  const glyph = (
    <SectionGlyph
      aria-label={tooltipText ?? t("Policy status")}
      background={background}
    >
      <HugeiconsIcon icon={icon} size={size} color={color} />
    </SectionGlyph>
  );

  if (!tooltipText) return glyph;

  return (
    <Tooltip title={tooltipText} arrow>
      <span style={{ display: "inline-flex" }}>{glyph}</span>
    </Tooltip>
  );
};

export default PolicyIcon;
